import React, { useRef, useState } from "react";
import { adminApi, mediaUrl } from "../../api";
import { UploadCloud, Loader2, ImageIcon } from "lucide-react";

// Read pixel size of a local file without uploading it.
const readSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => { resolve({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
    img.src = url;
  });

/**
 * hint: recommended size text. minW/minH: warn (not block) when the chosen file is smaller.
 */
export default function ImageUpload({ value, onChange, label = "Image", hint, minWidth, minHeight }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setWarning("");
    const warns = [];
    if (file.size > 5 * 1024 * 1024) warns.push(`This file is ${(file.size / 1048576).toFixed(1)} MB — large files slow the page down. Try compressing it.`);
    if (minWidth || minHeight) {
      const dim = await readSize(file);
      if (dim && ((minWidth && dim.w < minWidth) || (minHeight && dim.h < minHeight))) {
        warns.push(`This image is ${dim.w} × ${dim.h} px, smaller than the recommended minimum (${minWidth || "?"} × ${minHeight || "?"} px). It may look blurry on large screens.`);
      }
    }
    setWarning(warns.join(" "));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (e) {
      setError("Upload failed. Try a PNG/JPG/WebP image.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <p className="text-sm font-medium text-[#0f1e33] mb-1.5">{label}</p>}
      <div className="flex items-center gap-4">
        <div className="h-20 w-28 rounded-lg border border-border bg-[#f1f7fb] overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={mediaUrl(value)} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-[#9aa8b5]" />
          )}
        </div>
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#0e7490] border border-[#cfe6ec] rounded-lg px-4 h-10 hover:bg-[#eaf4f7] transition-colors"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload image"}
          </button>
          {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
          {warning && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1.5">{warning}</p>}
          <p className="text-xs text-[#5a6b7e] mt-1.5">{hint || "PNG, JPG or WebP"}</p>
          {value && <p className="text-xs text-emerald-600 mt-0.5">Image set</p>}
        </div>
      </div>
    </div>
  );
}
